/**
 * Keepsy Standorte (standorte.html): Liste zuerst, Leaflet-Karte als Ergaenzung.
 *
 * Daten: data/locations.json (nur Name, Stadt, Bundesland, Status, Art, Koordinate; keine
 * Adressen, keine Oeffnungszeiten). Die Zeilen stehen zusaetzlich als statisches HTML in
 * standorte.html (erzeugt mit rowHtml), damit die Liste ohne JavaScript und ohne Karte
 * vollstaendig ist (a11y S-29). Dieses Modul rendert sie aus der JSON-Datei neu und ergaenzt
 * Suche (Name, Stadt), Status-Filter mit Zaehlern, "In meiner Naehe" und die Karte.
 *
 * Karte: Leaflet 1.9.4 aus vendor/leaflet (globales L, klassisches Script vor diesem Modul),
 * OSM-Tiles mit Attribution, eigene DivIcon-Pins in Huellenform (offen: Koralle mit Haken,
 * bald: Gelb mit Ink-Kontur und Sticker "Bald", wie im Startseiten-Teaser). Bei Reduced
 * Motion ohne Zoom-, Fade- und Flug-Animation
 * (RESEARCH 7, Regel 9). Auf Touch-Geraeten wird die Karte erst nach "Karte bewegen" ziehbar,
 * damit die Seite scrollbar bleibt (a11y S-36). Die Karte startet erst, wenn sie in die Naehe
 * des sichtbaren Bereichs kommt, so laedt mobil niemand Kacheln, der sie nie sieht.
 *
 * URL: ?ort=<id> hebt einen Standort hervor und zeigt ihn auf der Karte. "Auf Karte zeigen"
 * und ein Klick auf einen Pin schreiben die Auswahl per replaceState in die URL.
 *
 * Die reinen Funktionen (rowHtml, matchesQuery, distanceKm, ...) laufen auch in Node.
 * @module pages/locations
 */

import { getLocations } from '@keepsy/data.js'
import { pageUrl } from '@keepsy/routes.js'
import { escapeHtml, debounce, NBSP } from '@keepsy/format.js'
import { icon, ICONS } from '@keepsy/ui-icons.js'

/* --------------------------------------------------------------------------
   Konstanten und Texte
   -------------------------------------------------------------------------- */

/** OpenStreetMap-Standardkacheln (einzige externe Quelle, Schriften sind selbst gehostet). */
export const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'

/** Pflicht-Attribution der OSM-Kacheln. */
export const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>-Mitwirkende'

/** Einziger erlaubter Ortshinweis fuer offene Automaten (keine Adressen, keine Zeiten).
 *  "frag am Info-Point" bleibt beisammen (geschuetzte Leerzeichen), sonst steht "Info-Point." mobil allein in der Zeile. */
export const NOTE_OPEN = 'Im Center, frag\u00A0am\u00A0Info-Point.'

/** Hinweis fuer Automaten, die noch kommen. */
export const NOTE_SOON = 'Hier zieht bald ein Automat ein.'

/** Gesprochener Status fuer Pins und Ansagen. */
const STATUS_SPOKEN = Object.freeze({ open: 'offen', soon: 'kommt bald' })

/** Zoomstufe beim Zeigen eines einzelnen Standorts. */
const FOCUS_ZOOM = 15

/** Hoechste Zoomstufe fuer Uebersichten (alle, gefiltert, Naehe). */
const OVERVIEW_MAX_ZOOM = 12

/** Ab dieser Entfernung lohnt sich der Hinweis auf den Onlineshop. */
const FAR_AWAY_KM = 150

/** Texte fuer "In meiner Naehe". Was passiert ist, warum, was jetzt (RESEARCH 8.1). */
const GEO_MESSAGES = Object.freeze({
  denied: {
    text: 'Ohne deinen Standort geht es auch. Such einfach nach deiner Stadt oder schau auf die Karte.',
    retry: false,
  },
  unavailable: {
    text: 'Wir konnten dich gerade nicht orten. Versuch es gleich nochmal oder such nach deiner Stadt.',
    retry: true,
  },
  timeout: {
    text: 'Das Orten hat zu lange gedauert. Versuch es nochmal oder such nach deiner Stadt.',
    retry: true,
  },
  unsupported: {
    text: 'Dein Browser verrät uns deinen Standort nicht. Such einfach nach deiner Stadt.',
    retry: false,
  },
})

/* --------------------------------------------------------------------------
   Reine Helfer (auch in Node nutzbar)
   -------------------------------------------------------------------------- */

/**
 * Ist der Standort geoeffnet?
 * @param {{ status?: string }} loc
 * @returns {boolean}
 */
export function isOpen(loc) {
  return Boolean(loc) && loc.status === 'open'
}

/**
 * Ortshinweis zum Standort: offene Automaten im Center oder Warenhaus bekommen den Info-Point-Hinweis,
 * der Firmensitz steht ohne Zusatz da (dort gibt es kein Center und keinen Info-Point).
 * @param {{ status?: string, kind?: string }} loc
 * @returns {string} Hinweis oder leer
 */
export function noteFor(loc) {
  if (!isOpen(loc)) return NOTE_SOON
  return loc.kind === 'Firmensitz' ? '' : NOTE_OPEN
}

const UMLAUTS = Object.freeze({ ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' })

/**
 * Kleinbuchstaben ohne Akzente, Sonderzeichen werden zu Leerzeichen.
 * @param {string} text
 * @returns {string}
 */
function fold(text) {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/**
 * Suchvarianten eines Textes: "Köln" ergibt "koln" und "koeln", damit "Koeln", "Köln"
 * und "Koln" gleichermassen treffen.
 * @param {unknown} text
 * @returns {string[]}
 */
export function searchVariants(text) {
  const lower = String(text ?? '').normalize('NFC').toLowerCase()
  const plain = fold(lower.replace(/ß/g, 'ss'))
  const expanded = fold(lower.replace(/[äöüß]/g, (ch) => UMLAUTS[ch]))
  return Array.from(new Set([plain, expanded])).filter(Boolean)
}

/**
 * Trifft die Suche den Standort? Gesucht wird in Name und Stadt. Jedes Wort der Suche muss
 * vorkommen, Gross- und Kleinschreibung und Umlaut-Schreibweisen sind egal.
 * @param {{ name?: string, city?: string }} loc
 * @param {string} query
 * @returns {boolean}
 */
export function matchesQuery(loc, query) {
  const variants = searchVariants(query)
  if (variants.length === 0) return true
  const haystack = [loc.name, loc.city].flatMap(searchVariants).join(' | ')
  return variants.some((variant) => variant.split(' ').every((token) => haystack.includes(token)))
}

/**
 * Luftlinie zwischen zwei Koordinaten in Kilometern (Haversine).
 * @param {{ lat: number, lng: number }} a
 * @param {{ lat: number, lng: number }} b
 * @returns {number}
 */
export function distanceKm(a, b) {
  const toRad = (deg) => (deg * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h)))
}

const KM_PRECISE = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 })
const KM_ROUND = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 })

/**
 * Entfernung als Text: "unter 1 km", "3,4 km", "128 km" (mit geschuetztem Leerzeichen).
 * @param {number} km
 * @returns {string}
 */
export function formatKm(km) {
  if (!Number.isFinite(km)) return ''
  if (km < 1) return `unter 1${NBSP}km`
  return `${(km < 10 ? KM_PRECISE : KM_ROUND).format(km)}${NBSP}km`
}

/**
 * Routenplaner von OpenStreetMap mit dem Center als Ziel. Der Start bleibt leer, den
 * traegt man dort selbst ein (wir kennen und senden keinen Standort).
 * @param {{ lat: number, lng: number }} loc
 * @returns {string}
 */
export function routeUrl(loc) {
  const lat = Number(loc.lat)
  const lng = Number(loc.lng)
  return `https://www.openstreetmap.org/directions?route=%3B${lat}%2C${lng}#map=17/${lat}/${lng}`
}

/**
 * Link "Route" zum Center. Nur fuer offene Standorte, an geplanten steht noch kein Automat.
 * @param {{ name: string, lat: number, lng: number }} loc
 * @param {string} className
 * @returns {string}
 */
function routeLinkHtml(loc, className) {
  const name = escapeHtml(loc.name)
  return `<a class="k-link ${className}" href="${escapeHtml(routeUrl(loc))}" target="_blank" rel="noopener">Route<span class="k-visually-hidden"> zu ${name} planen, öffnet OpenStreetMap in neuem Tab</span>${icon('arrow-right', { size: 18 })}</a>`
}

/** Huellen-Silhouette mit Spitze unten, 40 mal 52, Spitze bei (20, 50). */
const PIN_PATH = 'M13 3h14a9 9 0 0 1 9 9v22a9 9 0 0 1-9 9h-1.5L20 50l-5.5-7H13a9 9 0 0 1-9-9V12a9 9 0 0 1 9-9Z'

/**
 * Pin als SVG: Huelle mit Kamera-Ecke. Offen: Koralle mit Haken, bald: Gelb mit Ink-Kontur
 * und Plus (wie die Status-Chips der Startseite). Pfade aus icons.js, Farben aus
 * locations.css (Tokens). mark: false laesst das Zeichen weg (Karte: dort traegt der
 * Sticker "Bald" den Status).
 * @param {'open' | 'soon' | string} status
 * @param {{ mark?: boolean, className?: string }} [options]
 * @returns {string}
 */
export function pinSvg(status, options = {}) {
  const open = status === 'open'
  const glyph = open ? ICONS.check : ICONS.plus
  const mark = options.mark === false
    ? ''
    : `<g class="k-pin__mark${open ? '' : ' k-pin__mark--soon'}" transform="translate(10.4 16.6) scale(0.8)" fill="none" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round">${glyph}</g>`
  const cls = ['k-pin__svg', open ? 'k-pin__svg--open' : 'k-pin__svg--soon', options.className].filter(Boolean).join(' ')
  return `<svg class="${cls}" viewBox="0 0 40 52" width="40" height="52" aria-hidden="true" focusable="false"><path class="k-pin__body" d="${PIN_PATH}"/><rect class="k-pin__cam" x="9" y="8" width="9" height="9" rx="3"/>${mark}</svg>`
}

/**
 * Status-Sticker: "Offen" (Koralle 700 mit Haken) oder "Bald" (Creme mit Ink-Rand und Plus).
 * Status steckt in Text und Icon, nie nur in der Farbe (a11y S-31).
 * @param {'open' | 'soon' | string} status
 * @param {string} [className]
 * @returns {string}
 */
export function statusStickerHtml(status, className = '') {
  const cls = className ? ` ${className}` : ''
  return status === 'open'
    ? `<span class="k-sticker k-sticker--new k-loc-status${cls}">${icon('check', { size: 14 })}Offen</span>`
    : `<span class="k-sticker k-sticker--soon k-loc-status${cls}">${icon('plus', { size: 14 })}Bald</span>`
}

/**
 * Eine Zeile der Standortliste. Gleiches Markup im statischen HTML und zur Laufzeit.
 * Der Status-Sticker steht im DOM nach dem Namen und sitzt per CSS in eigener Zeile darueber.
 * "Route" gibt es nur bei offenen Standorten.
 * @param {{ id: string, name: string, city: string, state: string, status: string, kind: string, lat: number, lng: number }} loc
 * @param {{ interactive?: boolean }} [options] interactive false: Karten-Button versteckt (ohne JS)
 * @returns {string}
 */
export function rowHtml(loc, options = {}) {
  const interactive = options.interactive !== false
  const open = isOpen(loc)
  const id = escapeHtml(loc.id)
  const name = escapeHtml(loc.name)
  const city = escapeHtml(loc.city)
  const state = escapeHtml(loc.state)
  const kind = escapeHtml(loc.kind)
  // Im HTML als &nbsp; (lesbar im Quelltext von standorte.html und main-locations.liquid)
  const note = noteFor(loc).replace(/\u00A0/g, '&nbsp;')
  const showButton = `<button type="button" class="k-button k-button--secondary k-loc-row__show" data-loc-action="show"${interactive ? '' : ' hidden'}>${icon('pin', { size: 18 })}<span class="k-button__label">Auf Karte zeigen<span class="k-visually-hidden">: ${name}</span></span></button>`
  const route = open ? `
              ${routeLinkHtml(loc, 'k-loc-row__route')}` : ''
  // Ohne JS bleibt bei geplanten Standorten nichts Sichtbares uebrig, dann keine leere Zeile
  const actions = interactive || open
    ? `
            <div class="k-loc-row__actions">
              ${showButton}${route}
            </div>`
    : ''
  return `<li class="k-loc-row" id="k-ort-${id}" data-loc-id="${id}" data-status="${open ? 'open' : 'soon'}" data-name="${name}" data-city="${city}" data-state="${state}" data-kind="${kind}" data-lat="${Number(loc.lat)}" data-lng="${Number(loc.lng)}">
            <span class="k-loc-row__pin">${pinSvg(loc.status)}</span>
            <div class="k-loc-row__body">
              <div class="k-loc-row__head">
                <h3 class="k-loc-row__name">${name}</h3>
                ${statusStickerHtml(loc.status)}
              </div>
              <p class="k-loc-row__meta">${city}, ${state}<span class="k-loc-row__distance" data-loc-distance hidden></span></p>
              <p class="k-loc-row__note">${kind}${note ? `<span class="k-loc-row__sep" aria-hidden="true">·</span><span class="k-visually-hidden">. </span>${note}` : ''}</p>
            </div>${actions}
          </li>`
}

/**
 * Prueft und bereinigt Rohdaten aus locations.json.
 * @param {unknown} list
 * @returns {Array<{ id: string, name: string, city: string, state: string, status: 'open' | 'soon', kind: string, lat: number, lng: number }>}
 */
export function cleanLocations(list) {
  if (!Array.isArray(list)) return []
  const seen = new Set()
  const out = []
  for (const raw of list) {
    if (!raw || typeof raw !== 'object') continue
    const lat = Number(raw.lat)
    const lng = Number(raw.lng)
    const id = String(raw.id || '').trim()
    if (!id || seen.has(id) || !raw.name || !Number.isFinite(lat) || !Number.isFinite(lng)) continue
    seen.add(id)
    out.push({
      id,
      name: String(raw.name),
      city: String(raw.city || ''),
      state: String(raw.state || ''),
      status: raw.status === 'open' ? 'open' : 'soon',
      kind: String(raw.kind || 'Einkaufszentrum'),
      lat,
      lng,
    })
  }
  return out
}

/* --------------------------------------------------------------------------
   Laufzeit (nur im Browser)
   -------------------------------------------------------------------------- */

/** Elemente der Seite. */
const ui = {}

/** Zustand der Seite. Eine Quelle, aus der Liste, Zaehler und Karte folgen. */
const state = {
  /** @type {ReturnType<typeof cleanLocations>} */
  locations: [],
  /** @type {Map<string, { loc: any, row: HTMLElement, index: number, marker: any, distance: number | null }>} */
  entries: new Map(),
  query: '',
  /** @type {'all' | 'open' | 'soon'} */
  filter: 'all',
  /** @type {'default' | 'distance'} */
  sort: 'default',
  /** @type {{ lat: number, lng: number } | null} */
  user: null,
  selectedId: null,
  openPopupId: null,
  popupKeyboardFocus: false,
  showSeq: 0,
  map: null,
  markerLayer: null,
  userMarker: null,
  mapObserver: null,
  tileErrorShown: false,
  locating: false,
}

const reduceQuery = typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia('(prefers-reduced-motion: reduce)')
  : null
const desktopQuery = typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia('(min-width: 64rem)')
  : null

/** @returns {boolean} */
function reduceMotion() {
  return Boolean(reduceQuery && reduceQuery.matches)
}

/** @returns {ScrollBehavior} */
function scrollBehavior() {
  return reduceMotion() ? 'auto' : 'smooth'
}

/**
 * Untere Kante des Sticky-Headers, damit Sichtbarkeitspruefungen ihn beruecksichtigen.
 * @returns {number}
 */
function headerBottom() {
  const header = document.getElementById('k-header')
  if (!header) return 0
  return Math.max(0, header.getBoundingClientRect().bottom)
}

/**
 * Liegt das Element vollstaendig im sichtbaren Bereich (unter dem Header)?
 * @param {Element} el
 * @returns {boolean}
 */
function fullyVisible(el) {
  const rect = el.getBoundingClientRect()
  return rect.top >= headerBottom() && rect.bottom <= window.innerHeight
}

/**
 * Scrollt nur, wenn das Element nicht schon ganz zu sehen ist.
 * @param {Element} el
 * @param {ScrollLogicalPosition} block
 * @param {ScrollBehavior} [behavior]
 */
function revealElement(el, block, behavior = scrollBehavior()) {
  if (!el || fullyVisible(el)) return
  el.scrollIntoView({ block, behavior })
}

/**
 * Sagt einen Text in der unsichtbaren Live-Region an (fuer Karten-Aktionen).
 * @param {string} text
 */
function announce(text) {
  if (!ui.announce) return
  ui.announce.textContent = ''
  window.setTimeout(() => {
    ui.announce.textContent = text
  }, 60)
}

/**
 * Liest die Standorte aus dem statischen HTML, falls die JSON-Datei nicht laedt.
 * @param {HTMLElement} list
 * @returns {ReturnType<typeof cleanLocations>}
 */
function readFromDom(list) {
  return cleanLocations(Array.from(list.querySelectorAll('[data-loc-id]')).map((row) => ({
    id: row.dataset.locId,
    name: row.dataset.name,
    city: row.dataset.city,
    state: row.dataset.state,
    status: row.dataset.status,
    kind: row.dataset.kind,
    lat: row.dataset.lat,
    lng: row.dataset.lng,
  })))
}

/* --------------------------------------------------------------------------
   Liste, Filter, Suche
   -------------------------------------------------------------------------- */

/** Rendert alle Zeilen aus den Daten und verknuepft sie mit dem Zustand. */
function renderRows() {
  ui.list.innerHTML = state.locations.map((loc) => rowHtml(loc)).join('')
  state.entries = new Map()
  state.locations.forEach((loc, index) => {
    const row = ui.list.querySelector(`[data-loc-id="${CSS.escape(loc.id)}"]`)
    state.entries.set(loc.id, { loc, row, index, marker: null, distance: null })
  })
}

/** @returns {Array<{ loc: any, row: HTMLElement, index: number, marker: any, distance: number | null }>} */
function orderedEntries() {
  const list = Array.from(state.entries.values())
  if (state.sort === 'distance' && state.user) {
    return list.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity))
  }
  return list.sort((a, b) => a.index - b.index)
}

/** Bringt die Zeilen in die Reihenfolge der Sortierung (nur wenn sie sich aendert). */
function ensureOrder() {
  const wanted = orderedEntries().map((entry) => entry.row)
  const current = Array.from(ui.list.children)
  const same = wanted.length === current.length && wanted.every((row, i) => row === current[i])
  if (same) return
  const fragment = document.createDocumentFragment()
  wanted.forEach((row) => fragment.appendChild(row))
  ui.list.appendChild(fragment)
}

/**
 * Aktuell sichtbare Eintraege in Listenreihenfolge.
 * @returns {Array<{ loc: any, row: HTMLElement, marker: any, distance: number | null }>}
 */
function visibleEntries() {
  return orderedEntries().filter((entry) => !entry.row.hidden)
}

/**
 * Text der Ergebniszeile.
 * @param {number} n sichtbare Standorte
 * @returns {string}
 */
function resultText(n) {
  const q = state.query.trim()
  let what
  if (n === 0) {
    what = state.filter === 'open' ? 'kein offener Standort' : state.filter === 'soon' ? 'kein Standort, der bald kommt' : 'kein Standort'
  } else if (state.filter === 'open') {
    what = n === 1 ? '1 offener Standort' : `${n} offene Standorte`
  } else if (state.filter === 'soon') {
    what = n === 1 ? '1 Standort kommt bald' : `${n} Standorte kommen bald`
  } else {
    what = n === 1 ? '1 Standort' : `${n} Standorte`
  }
  let text
  if (q) text = `Für „${q}“: ${what}`
  else text = state.filter === 'all' ? `Alle ${what}` : what.charAt(0).toUpperCase() + what.slice(1)
  if (state.sort === 'distance' && state.user && n > 1) text += ', nach Entfernung sortiert'
  return `${text}.`
}

/** Leerzustand, wenn Suche und Filter nichts uebrig lassen. */
function renderEmpty(countAllForQuery) {
  const q = state.query.trim()
  const filterWord = state.filter === 'open' ? 'offenen ' : state.filter === 'soon' ? 'geplanten ' : ''
  let text = q
    ? `Für „${escapeHtml(q)}“ haben wir noch keinen ${filterWord}Automaten.`
    : `Gerade gibt es keinen ${filterWord}Automaten.`
  const nearest = nearestEntries()[0]
  if (nearest) {
    text += ` Am nächsten an dir ist ${escapeHtml(nearest.loc.name)}, ca. ${formatKm(nearest.distance)}.`
  }
  text += ' Online bist du sowieso in drei Minuten fertig.'
  const actions = []
  if (q) actions.push('<button type="button" class="k-button k-button--secondary" data-loc-action="clear-search">Suche löschen</button>')
  if (state.filter !== 'all' && countAllForQuery > 0) {
    actions.push(`<button type="button" class="k-button k-button--secondary" data-loc-action="show-all">Alle ${countAllForQuery} Treffer zeigen</button>`)
  }
  actions.push(`<a class="k-link" href="${escapeHtml(pageUrl('configurator'))}">Jetzt gestalten${icon('arrow-right', { size: 18 })}</a>`)
  ui.empty.innerHTML = `
    <p class="k-loc-empty__title">Hier sind wir noch nicht.</p>
    <p class="k-loc-empty__text">${text}</p>
    <div class="k-loc-empty__actions">${actions.join('')}</div>`
}

/**
 * Wendet Suche, Filter und Sortierung an: Zeilen, Zaehler, Ergebniszeile, Leerzustand, Pins.
 * @param {{ fit?: boolean }} [options] fit: Karte auf das Ergebnis einpassen
 */
function applyFilters(options = {}) {
  const matching = state.locations.filter((loc) => matchesQuery(loc, state.query))
  const counts = {
    all: matching.length,
    open: matching.filter(isOpen).length,
    soon: matching.filter((loc) => !isOpen(loc)).length,
  }
  for (const chip of ui.chips) {
    const key = chip.dataset.locFilter
    chip.setAttribute('aria-pressed', String(key === state.filter))
    const count = chip.querySelector('[data-loc-count]')
    if (count) count.textContent = String(counts[key] ?? 0)
  }

  const visibleIds = new Set(
    matching.filter((loc) => state.filter === 'all' || loc.status === state.filter).map((loc) => loc.id),
  )
  ensureOrder()
  for (const entry of state.entries.values()) {
    entry.row.hidden = !visibleIds.has(entry.loc.id)
  }

  const n = visibleIds.size
  ui.list.hidden = n === 0
  ui.empty.hidden = n > 0
  if (n === 0) renderEmpty(counts.all)
  ui.result.textContent = resultText(n)
  ui.resetSort.hidden = !(state.sort === 'distance' && state.user)

  updateMarkers()
  if (options.fit) scheduleFit()
}

/** Suche verzoegert anwenden, damit Liste und Karte beim Tippen nicht flackern. */
const applySearch = debounce(() => applyFilters({ fit: true }), 150)

/**
 * Setzt den Status-Filter.
 * @param {'all' | 'open' | 'soon'} filter
 */
function setFilter(filter) {
  if (!['all', 'open', 'soon'].includes(filter) || filter === state.filter) return
  state.filter = filter
  applyFilters({ fit: true })
}

/** Leert die Suche und gibt den Fokus ins Suchfeld. */
function clearSearch() {
  ui.search.value = ''
  state.query = ''
  ui.searchClear.hidden = true
  applySearch.cancel()
  applyFilters({ fit: true })
  ui.search.focus()
}

/* --------------------------------------------------------------------------
   Auswahl und URL
   -------------------------------------------------------------------------- */

/**
 * Schreibt die Auswahl als ?ort= in die URL, ohne neuen History-Eintrag.
 * @param {string | null} id
 */
function updateUrl(id) {
  try {
    const url = new URL(window.location.href)
    if (id) url.searchParams.set('ort', id)
    else url.searchParams.delete('ort')
    window.history.replaceState(window.history.state, '', url)
  } catch {
    // Ohne History-API bleibt die URL einfach, wie sie ist.
  }
}

/**
 * Markiert einen Standort in der Liste (gelb hinterlegt) und in der URL.
 * @param {string} id
 * @param {{ source?: 'list' | 'map' | 'url' }} [options]
 */
function selectLocation(id, options = {}) {
  const entry = state.entries.get(id)
  if (!entry) return
  state.selectedId = id
  for (const item of state.entries.values()) {
    item.row.classList.toggle('is-selected', item.loc.id === id)
  }
  if (options.source !== 'url') updateUrl(id)
  if (options.source === 'map' && desktopQuery && desktopQuery.matches && !entry.row.hidden) {
    revealElement(entry.row, 'nearest')
  }
}

/* --------------------------------------------------------------------------
   Karte
   -------------------------------------------------------------------------- */

/**
 * Zeigt statt der Karte einen ruhigen Hinweis (Leaflet fehlt oder ist abgestuerzt).
 * @param {string} text
 */
function showMapFallback(text) {
  if (!ui.mapCanvas) return
  ui.mapCanvas.innerHTML = `<p class="k-loc-map__fallback">${escapeHtml(text)} <a href="#k-loc-list">Zur Liste</a></p>`
}

/**
 * Popup-Inhalt eines Standorts. Titel ist fokussierbar, damit Tastatur und Screenreader
 * nach "Auf Karte zeigen" direkt im Popup landen.
 * @param {{ loc: any, distance: number | null }} entry
 * @returns {string}
 */
function popupHtml(entry) {
  const loc = entry.loc
  const name = escapeHtml(loc.name)
  const distance = Number.isFinite(entry.distance) ? `<span aria-hidden="true"> · </span><span class="k-visually-hidden">, </span>ca. ${formatKm(entry.distance)}` : ''
  return `<div class="k-pop" data-loc-popup="${escapeHtml(loc.id)}">
    ${statusStickerHtml(loc.status, 'k-pop__status')}
    <p class="k-pop__title" tabindex="-1">${name}</p>
    <p class="k-pop__meta">${escapeHtml(loc.city)}, ${escapeHtml(loc.state)}${distance}</p>
    <p class="k-pop__note">${escapeHtml(loc.kind)}.${noteFor(loc) ? ` ${noteFor(loc)}` : ''}</p>
    <div class="k-pop__actions">
      ${isOpen(loc) ? routeLinkHtml(loc, 'k-pop__route') : ''}
      <button type="button" class="k-text-button k-pop__back" data-loc-action="to-list">Zur Liste</button>
    </div>
  </div>`
}

/**
 * DivIcon eines Standorts: 44 mal 56 px, Spitze unten in der Mitte.
 * @param {any} L
 * @param {{ status: string }} loc
 */
function pinIcon(L, loc) {
  const open = isOpen(loc)
  const sticker = open ? '' : '<span class="k-pin__sticker">Bald</span>'
  return L.divIcon({
    className: `k-pin ${open ? 'k-pin--open' : 'k-pin--soon'}`,
    html: `<span class="k-pin__shape">${pinSvg(loc.status, { mark: open })}${sticker}</span>`,
    iconSize: [44, 56],
    iconAnchor: [22, 52],
    popupAnchor: [0, -48],
  })
}

/**
 * Deutsches Label am Pin (Leaflet setzt bei DivIcons kein alt). Leaflet vergibt selbst
 * tabindex="0" und role="button".
 * @param {{ loc: any, marker: any }} entry
 */
function labelMarker(entry) {
  const el = entry.marker && entry.marker.getElement()
  if (!el) return
  const loc = entry.loc
  el.setAttribute('aria-label', `${loc.name}, ${loc.city}, ${STATUS_SPOKEN[loc.status] || ''}`)
  el.removeAttribute('title')
  el.classList.toggle('is-active', state.openPopupId === loc.id)
}

/**
 * Hebt den Pin mit offenem Popup hervor.
 * @param {string | null} id
 */
function setActiveMarker(id) {
  for (const entry of state.entries.values()) {
    if (!entry.marker) continue
    const active = entry.loc.id === id
    const el = entry.marker.getElement()
    if (el) el.classList.toggle('is-active', active)
    entry.marker.setZIndexOffset(active ? 1000 : 0)
  }
}

/**
 * Zielhoehe des Pins in Kartenpixeln: etwas unter der Mitte des Ausschnitts, damit das
 * Popup darueber ohne Nachschieben ins Bild passt (wichtig bei Reduced Motion ohne Auto-Pan).
 * @param {number} top sichtbarer Bereich oben (Kartenpixel)
 * @param {number} bottom sichtbarer Bereich unten (Kartenpixel)
 * @returns {number}
 */
function anchorWithin(top, bottom) {
  const visible = bottom - top
  return top + visible / 2 + Math.min(120, Math.max(0, visible / 2 - 40))
}

/**
 * Pin-Hoehe fuer den gerade sichtbaren Teil der Karte, oder null, wenn zu wenig Karte
 * im Bild ist (dann wird zuerst gescrollt). So scrollt die Seite auf dem Desktop nicht,
 * nur weil die Kartenmitte knapp unter dem sichtbaren Bereich liegt.
 * @returns {number | null}
 */
function visibleAnchor() {
  const rect = ui.mapCanvas.getBoundingClientRect()
  const top = Math.max(0, headerBottom() - rect.top)
  const bottom = Math.min(rect.height, window.innerHeight - rect.top)
  if (bottom - top < Math.min(rect.height, 360)) return null
  return anchorWithin(top, bottom)
}

/**
 * Kartenmitte, bei der der Standort auf Hoehe anchorY sitzt.
 * @param {{ lat: number, lng: number }} loc
 * @param {number} zoom
 * @param {number} [anchorY] Pin-Hoehe in Kartenpixeln (Standard: ganze Karte sichtbar)
 */
function focusCenter(loc, zoom, anchorY) {
  const map = state.map
  const L = window.L
  const height = map.getSize().y
  const y = Number.isFinite(anchorY) ? anchorY : anchorWithin(0, height)
  const point = map.project(L.latLng(loc.lat, loc.lng), zoom).add(L.point(0, height / 2 - y))
  return map.unproject(point, zoom)
}

/** Passt die Karte auf die sichtbaren Standorte ein. */
function fitVisible() {
  const map = state.map
  if (!map) return
  const L = window.L
  const points = visibleEntries().map((entry) => L.latLng(entry.loc.lat, entry.loc.lng))
  if (points.length === 0) return
  const animate = !reduceMotion()
  if (points.length === 1) {
    map.setView(points[0], OVERVIEW_MAX_ZOOM, { animate })
    return
  }
  map.fitBounds(L.latLngBounds(points), { padding: [48, 48], maxZoom: OVERVIEW_MAX_ZOOM, animate })
}

const scheduleFit = debounce(fitVisible, 280)

/**
 * Passt die Karte auf die eigene Position und die zwei naechsten sichtbaren Standorte ein.
 * @param {{ animate?: boolean }} [options]
 */
function fitUser(options = {}) {
  const map = state.map
  if (!map || !state.user) return
  const L = window.L
  const near = visibleEntries()
    .filter((entry) => Number.isFinite(entry.distance))
    .sort((a, b) => a.distance - b.distance)
    .slice(0, 2)
  const points = [L.latLng(state.user.lat, state.user.lng), ...near.map((entry) => L.latLng(entry.loc.lat, entry.loc.lng))]
  map.fitBounds(L.latLngBounds(points), {
    padding: [56, 56],
    maxZoom: OVERVIEW_MAX_ZOOM,
    animate: options.animate !== false && !reduceMotion(),
  })
}

/** Zeigt oder versteckt Pins passend zu Suche und Filter. */
function updateMarkers() {
  if (!state.map || !state.markerLayer) return
  for (const entry of state.entries.values()) {
    if (!entry.marker) continue
    const visible = !entry.row.hidden
    const onMap = state.markerLayer.hasLayer(entry.marker)
    if (visible && !onMap) state.markerLayer.addLayer(entry.marker)
    if (!visible && onMap) {
      if (state.openPopupId === entry.loc.id) state.map.closePopup()
      state.markerLayer.removeLayer(entry.marker)
    }
  }
}

/** Setzt oder verschiebt den Punkt "Du bist ungefaehr hier". */
function placeUserMarker() {
  const map = state.map
  if (!map || !state.user) return
  const L = window.L
  const latLng = L.latLng(state.user.lat, state.user.lng)
  if (state.userMarker) {
    state.userMarker.setLatLng(latLng)
    return
  }
  state.userMarker = L.marker(latLng, {
    icon: L.divIcon({
      className: 'k-loc-me',
      html: '<span class="k-loc-me__dot"></span><span class="k-loc-me__label">Du</span>',
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    }),
    keyboard: false,
    interactive: false,
    zIndexOffset: -100,
  }).addTo(map)
  const el = state.userMarker.getElement()
  if (el) el.setAttribute('aria-hidden', 'true')
}

/** Ein Steuerelement fuer Touch: Karte erst nach Tipp ziehbar (a11y S-36). */
function createMoveControl(L) {
  const Control = L.Control.extend({
    options: { position: 'topright' },
    onAdd() {
      const button = L.DomUtil.create('button', 'k-loc-move')
      button.type = 'button'
      button.setAttribute('aria-pressed', 'false')
      button.innerHTML = '<span class="k-loc-move__dot" aria-hidden="true"></span>Karte bewegen'
      L.DomEvent.disableClickPropagation(button)
      L.DomEvent.on(button, 'click', () => {
        const map = state.map
        if (!map) return
        const on = button.getAttribute('aria-pressed') !== 'true'
        button.setAttribute('aria-pressed', String(on))
        if (on) map.dragging.enable()
        else map.dragging.disable()
        announce(on ? 'Karte lässt sich jetzt mit einem Finger ziehen.' : 'Karte ist wieder fest. Die Seite scrollt normal.')
      })
      return button
    },
  })
  return new Control()
}

/** Popup-Elemente, deren Ereignisse schon gebunden sind. */
const boundPopups = new WeakSet()

/**
 * Bindet Ereignisse eines Popups einmal pro Popup-Element.
 * @param {HTMLElement} el
 */
function bindPopupElement(el) {
  if (boundPopups.has(el)) return
  boundPopups.add(el)
  el.addEventListener('focusin', (event) => {
    const target = /** @type {HTMLElement} */ (event.target)
    state.popupKeyboardFocus = Boolean(target && target.matches && target.matches(':focus-visible'))
  })
  el.addEventListener('focusout', (event) => {
    if (!el.contains(/** @type {Node | null} */ (event.relatedTarget))) state.popupKeyboardFocus = false
  })
  el.addEventListener('click', (event) => {
    const back = event.target.closest('[data-loc-action="to-list"]')
    if (!back) return
    event.preventDefault()
    const id = state.openPopupId
    state.popupKeyboardFocus = false
    if (state.map) state.map.closePopup()
    focusRow(id)
  })
}

/**
 * Fokus zurueck auf den Karten-Button der Zeile (oder die Liste).
 * @param {string | null} id
 */
function focusRow(id) {
  const entry = id ? state.entries.get(id) : null
  const target = entry && !entry.row.hidden ? entry.row.querySelector('[data-loc-action="show"]') : ui.list
  if (!target) return
  target.focus({ preventScroll: true })
  revealElement(target, 'center')
}

/** @param {any} event Leaflet popupopen */
function onPopupOpen(event) {
  const el = event.popup.getElement()
  if (!el) return
  const close = el.querySelector('.leaflet-popup-close-button')
  if (close) {
    close.setAttribute('aria-label', 'Infofenster schließen')
    close.removeAttribute('title')
  }
  bindPopupElement(el)
  const content = el.querySelector('[data-loc-popup]')
  const id = content ? content.getAttribute('data-loc-popup') : null
  state.openPopupId = id
  setActiveMarker(id)
  const entry = id ? state.entries.get(id) : null
  if (entry) {
    const loc = entry.loc
    announce(`${loc.name} in ${loc.city}, ${STATUS_SPOKEN[loc.status]}.${noteFor(loc) ? ` ${noteFor(loc)}` : ''}`)
  }
}

function onPopupClose() {
  const id = state.openPopupId
  const restore = state.popupKeyboardFocus
  state.openPopupId = null
  state.popupKeyboardFocus = false
  setActiveMarker(null)
  // Nur wer per Tastatur im Popup war, bekommt den Fokus zurueck in die Liste.
  // Wer tippt oder klickt, bleibt bei der Karte (kein Sprung nach oben).
  if (restore) focusRow(id)
}

/** Erste Ansicht: Deep Link vor eigener Position vor allen Standorten. */
function initialView() {
  const map = state.map
  const deepLink = state.selectedId ? state.entries.get(state.selectedId) : null
  if (deepLink && !deepLink.row.hidden) {
    const anchorY = visibleAnchor()
    map.setView(focusCenter(deepLink.loc, 13, anchorY === null ? undefined : anchorY), 13, { animate: false })
    deepLink.marker.openPopup()
    return
  }
  if (state.user) {
    fitUser({ animate: false })
    return
  }
  const L = window.L
  const points = visibleEntries().map((entry) => L.latLng(entry.loc.lat, entry.loc.lng))
  const all = points.length ? points : state.locations.map((loc) => L.latLng(loc.lat, loc.lng))
  map.fitBounds(L.latLngBounds(all), { padding: [40, 40], maxZoom: OVERVIEW_MAX_ZOOM, animate: false })
}

/** Baut die Karte. Wirft bei Fehlern, ensureMap faengt sie ab. */
function createMap() {
  const L = window.L
  const reduce = reduceMotion()
  const coarse = window.matchMedia ? window.matchMedia('(pointer: coarse)').matches : false

  ui.mapCanvas.innerHTML = ''
  const map = L.map(ui.mapCanvas, {
    zoomControl: false,
    attributionControl: false,
    scrollWheelZoom: false,
    dragging: !coarse,
    zoomAnimation: !reduce,
    fadeAnimation: !reduce,
    markerZoomAnimation: !reduce,
    inertia: !reduce,
    keyboard: true,
    minZoom: 4,
    maxZoom: 18,
  })
  state.map = map

  L.control.zoom({
    position: 'topleft',
    zoomInTitle: 'Karte vergrößern',
    zoomOutTitle: 'Karte verkleinern',
    zoomInText: icon('plus', { size: 20 }),
    zoomOutText: icon('minus', { size: 20 }),
  }).addTo(map)
  L.control.attribution({
    position: 'bottomright',
    // Kein Link nach aussen: Leaflet (BSD-2) steht im README, sichtbar bleibt die OSM-Pflichtangabe
    prefix: false,
  }).addTo(map)
  if (coarse) createMoveControl(L).addTo(map)

  state.markerLayer = L.featureGroup().addTo(map)
  for (const entry of state.entries.values()) {
    const marker = L.marker([entry.loc.lat, entry.loc.lng], {
      icon: pinIcon(L, entry.loc),
      keyboard: true,
      riseOnHover: true,
    })
    marker.bindPopup(() => popupHtml(entry), {
      className: 'k-pop-wrap',
      maxWidth: 280,
      minWidth: 220,
      autoPan: !reduce,
      autoPanPadding: L.point(24, 24),
      closeButton: true,
    })
    marker.on('add', () => labelMarker(entry))
    // Klick, Tipp und Enter auf dem Pin oeffnen das Popup, alle markieren dieselbe Zeile.
    marker.on('popupopen', (event) => {
      if (state.selectedId !== entry.loc.id) selectLocation(entry.loc.id, { source: 'map' })
      // Ohne Auto-Pan (Reduced Motion) springt die Karte ohne Animation, falls das Popup
      // am Rand abgeschnitten waere.
      if (!reduceMotion()) return
      const el = event.popup && event.popup.getElement()
      if (!el) return
      const box = el.getBoundingClientRect()
      const frame = ui.mapCanvas.getBoundingClientRect()
      const inside = box.top >= frame.top && box.bottom <= frame.bottom && box.left >= frame.left && box.right <= frame.right
      if (inside) return
      const zoom = map.getZoom()
      const anchorY = visibleAnchor()
      map.setView(focusCenter(entry.loc, zoom, anchorY === null ? undefined : anchorY), zoom, { animate: false })
    })
    entry.marker = marker
  }
  updateMarkers()

  map.on('popupopen', onPopupOpen)
  map.on('popupclose', onPopupClose)

  // Esc schliesst das Popup auch bei Fokus im Popup oder auf einem Pin. Leaflet selbst hoert
  // nur, solange der Kartencontainer direkt fokussiert ist. Aus dem Popup geht der Fokus
  // danach zurueck in die Liste (onPopupClose).
  ui.mapCanvas.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !state.openPopupId) return
    event.preventDefault()
    event.stopPropagation()
    const popup = map.getContainer().querySelector('.leaflet-popup')
    state.popupKeyboardFocus = Boolean(popup && popup.contains(document.activeElement))
    map.closePopup()
  })

  initialView()

  const tiles = L.tileLayer(TILE_URL, { maxZoom: 19, attribution: TILE_ATTRIBUTION })
  tiles.on('tileerror', () => {
    if (state.tileErrorShown) return
    state.tileErrorShown = true
    ui.mapNote.insertAdjacentHTML(
      'beforeend',
      ' <strong class="k-loc-map__offline">Die Kartenkacheln laden gerade nicht, die Pins stimmen trotzdem.</strong>',
    )
  })
  tiles.addTo(map)

  if (state.user) placeUserMarker()
}

/**
 * Startet die Karte, falls noch nicht geschehen.
 * @returns {any | null} Leaflet-Map oder null
 */
function ensureMap() {
  if (state.map) return state.map
  if (state.mapObserver) {
    state.mapObserver.disconnect()
    state.mapObserver = null
  }
  if (!ui.mapCanvas || typeof window.L === 'undefined') {
    showMapFallback('Die Karte lädt gerade nicht. Alle Standorte stehen in der Liste.')
    return null
  }
  try {
    createMap()
  } catch (err) {
    console.error('[keepsy/standorte] Karte konnte nicht starten.', err)
    if (state.map) {
      try { state.map.remove() } catch { /* bereits entfernt */ }
    }
    state.map = null
    showMapFallback('Die Karte lädt gerade nicht. Alle Standorte stehen in der Liste.')
    return null
  }
  return state.map
}

/** Startet die Karte erst, wenn sie in die Naehe des sichtbaren Bereichs kommt. */
function setupMap() {
  if (!ui.mapCanvas) return
  if (typeof window.L === 'undefined') {
    showMapFallback('Die Karte lädt gerade nicht. Alle Standorte stehen in der Liste.')
    return
  }
  const rect = ui.mapCanvas.getBoundingClientRect()
  const nearViewport = rect.top < window.innerHeight + 320 && rect.bottom > -320
  if (nearViewport || !('IntersectionObserver' in window)) {
    ensureMap()
    return
  }
  state.mapObserver = new IntersectionObserver((records) => {
    if (records.some((record) => record.isIntersecting)) ensureMap()
  }, { rootMargin: '320px 0px' })
  state.mapObserver.observe(ui.mapCanvas)
  // Rueckfallebene, falls der Observer (z. B. in gedrosselten Hintergrund-Tabs) nicht feuert
  const onScroll = () => {
    if (state.map) {
      window.removeEventListener('scroll', onScroll)
      return
    }
    const box = ui.mapCanvas.getBoundingClientRect()
    if (box.top < window.innerHeight + 320 && box.bottom > -320) {
      window.removeEventListener('scroll', onScroll)
      ensureMap()
    }
  }
  window.addEventListener('scroll', onScroll, { passive: true })
}

/**
 * Zeigt einen Standort auf der Karte: Karte ins Bild, hinfliegen (oder springen bei
 * Reduced Motion), Popup oeffnen, Fokus auf den Popup-Titel.
 * @param {string} id
 */
function showOnMap(id) {
  const entry = state.entries.get(id)
  if (!entry) return
  selectLocation(id, { source: 'list' })
  const map = ensureMap()
  if (!map || !entry.marker) {
    announce('Die Karte lädt gerade nicht. Alle Angaben stehen in der Liste.')
    return
  }
  scheduleFit.cancel()
  let anchorY = visibleAnchor()
  if (anchorY === null) {
    ui.mapFrame.scrollIntoView({ block: 'center', behavior: scrollBehavior() })
    anchorY = undefined
  }

  const seq = ++state.showSeq
  const target = focusCenter(entry.loc, FOCUS_ZOOM, anchorY)
  const open = () => {
    if (seq !== state.showSeq) return
    if (!state.markerLayer.hasLayer(entry.marker)) return
    entry.marker.openPopup()
    const popup = entry.marker.getPopup()
    const title = popup && popup.getElement() ? popup.getElement().querySelector('.k-pop__title') : null
    if (title) title.focus({ preventScroll: true })
  }

  if (state.openPopupId && state.openPopupId !== id) {
    state.popupKeyboardFocus = false
    map.closePopup()
  }

  if (reduceMotion()) {
    map.setView(target, FOCUS_ZOOM, { animate: false })
    open()
    return
  }

  const size = map.getSize()
  const offCenter = map.latLngToContainerPoint(target).distanceTo(size.divideBy(2))
  if (map.getZoom() === FOCUS_ZOOM && offCenter < 2) {
    open()
    return
  }
  let done = false
  const finish = () => {
    if (done) return
    done = true
    window.clearTimeout(timer)
    open()
  }
  const timer = window.setTimeout(finish, 2600)
  map.once('moveend', finish)
  map.flyTo(target, FOCUS_ZOOM, { duration: 1.1 })
}

/* --------------------------------------------------------------------------
   In meiner Naehe
   -------------------------------------------------------------------------- */

/** @returns {Array<{ loc: any, distance: number }>} alle Standorte nach Entfernung */
function nearestEntries() {
  if (!state.user) return []
  return Array.from(state.entries.values())
    .filter((entry) => Number.isFinite(entry.distance))
    .sort((a, b) => a.distance - b.distance)
}

/** Leert den Hinweisbereich unter den Werkzeugen. */
function clearHint() {
  ui.hint.innerHTML = ''
}

/**
 * Hinweis-Zeile (Gelb 100, Icon, Wort "Hinweis").
 * @param {{ text: string, retry?: boolean }} message
 */
function showHint(message) {
  const retry = message.retry
    ? '<button type="button" class="k-button k-button--ghost k-hint__action" data-loc-action="retry-locate">Nochmal versuchen</button>'
    : ''
  ui.hint.innerHTML = `<div class="k-hint k-loc-hint">${icon('info', { size: 20 })}<p><span class="k-hint__label">Hinweis:</span> ${escapeHtml(message.text)}</p>${retry}</div>`
}

/** Ergebnis von "In meiner Naehe": der naechste Automat mit Entfernung und Button. */
function showNearest() {
  const sorted = nearestEntries()
  const nearest = sorted[0]
  if (!nearest) return
  const loc = nearest.loc
  let text = `Am nächsten an dir: <strong>${escapeHtml(loc.name)}</strong>, ca. ${formatKm(nearest.distance)}`
  if (!isOpen(loc)) {
    text += ', kommt bald.'
    const nextOpen = sorted.find((entry) => isOpen(entry.loc))
    if (nextOpen) text += ` Schon offen: <strong>${escapeHtml(nextOpen.loc.name)}</strong>, ca. ${formatKm(nextOpen.distance)}.`
  } else {
    text += '.'
  }
  if (nearest.distance > FAR_AWAY_KM) {
    text += ' Ganz schön weit. Online bist du in drei Minuten fertig.'
  }
  ui.hint.innerHTML = `<div class="k-note">${icon('pin', { size: 20 })}<p>${text}</p><button type="button" class="k-button k-button--ghost k-note__action" data-loc-action="show-nearest" data-loc-target="${escapeHtml(loc.id)}">Auf Karte zeigen<span class="k-visually-hidden">: ${escapeHtml(loc.name)}</span></button></div>`
}

/**
 * Lade-Zustand des Buttons "In meiner Naehe".
 * @param {boolean} on
 */
function setLocating(on) {
  state.locating = on
  const button = ui.locate
  button.setAttribute('aria-busy', String(on))
  const label = button.querySelector('.k-button__label')
  const iconSlot = button.querySelector('[data-loc-locate-icon]')
  if (label) label.textContent = on ? 'Wir suchen dich' : 'In meiner Nähe'
  if (iconSlot) iconSlot.innerHTML = on ? '<span class="k-spinner" aria-hidden="true"></span>' : icon('pin', { size: 20 })
}

/** Fragt den Standort ab (freiwillig, nur im Browser, nichts wird gespeichert). */
function locate() {
  if (state.locating) return
  clearHint()
  if (!('geolocation' in navigator) || window.isSecureContext === false) {
    showHint(GEO_MESSAGES.unsupported)
    return
  }
  setLocating(true)
  navigator.geolocation.getCurrentPosition(onLocated, onLocateError, {
    enableHighAccuracy: false,
    timeout: 12000,
    maximumAge: 300000,
  })
}

/** @param {GeolocationPosition} position */
function onLocated(position) {
  setLocating(false)
  const user = { lat: position.coords.latitude, lng: position.coords.longitude }
  if (!Number.isFinite(user.lat) || !Number.isFinite(user.lng)) {
    showHint(GEO_MESSAGES.unavailable)
    return
  }
  state.user = user
  for (const entry of state.entries.values()) {
    entry.distance = distanceKm(user, entry.loc)
    const slot = entry.row.querySelector('[data-loc-distance]')
    if (slot) {
      slot.innerHTML = `<span class="k-loc-row__sep" aria-hidden="true">·</span><span class="k-visually-hidden">, </span>ca. ${formatKm(entry.distance)} entfernt`
      slot.hidden = false
    }
  }
  state.sort = 'distance'
  applyFilters({ fit: false })
  showNearest()
  if (state.map) {
    placeUserMarker()
    if (state.openPopupId) state.map.closePopup()
    fitUser()
  }
}

/** @param {GeolocationPositionError} error */
function onLocateError(error) {
  setLocating(false)
  const code = error && error.code
  if (code === 1) showHint(GEO_MESSAGES.denied)
  else if (code === 3) showHint(GEO_MESSAGES.timeout)
  else showHint(GEO_MESSAGES.unavailable)
}

/** Zurueck zur Standard-Reihenfolge (offene zuerst), Entfernungen bleiben stehen. */
function resetSort() {
  state.sort = 'default'
  applyFilters({ fit: false })
  ui.result.focus({ preventScroll: true })
}

/* --------------------------------------------------------------------------
   Start
   -------------------------------------------------------------------------- */

/** Liest ?ort= und hebt den Standort hervor (ohne Fokus zu stehlen). */
function handleDeepLink() {
  let id = null
  try {
    id = new URLSearchParams(window.location.search).get('ort')
  } catch {
    id = null
  }
  if (!id) return
  const entry = state.entries.get(id)
  if (!entry) {
    showHint({ text: `Diesen Standort kennen wir nicht. Hier sind alle ${state.locations.length}.` })
    updateUrl(null)
    return
  }
  selectLocation(id, { source: 'url' })
  revealElement(entry.row, 'center', 'auto')
  announce(`${entry.loc.name} ist hervorgehoben.`)
}

/** Verknuepft alle Bedienelemente. */
function bindControls() {
  ui.searchForm.addEventListener('submit', (event) => {
    event.preventDefault()
    applySearch.flush()
  })
  ui.search.addEventListener('input', () => {
    state.query = ui.search.value
    ui.searchClear.hidden = ui.search.value.length === 0
    applySearch()
  })
  ui.searchClear.addEventListener('click', clearSearch)

  for (const chip of ui.chips) {
    chip.addEventListener('click', () => setFilter(/** @type {any} */ (chip.dataset.locFilter)))
  }

  ui.locate.addEventListener('click', locate)
  ui.resetSort.addEventListener('click', resetSort)

  ui.root.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-loc-action]')
    if (!trigger || !ui.root.contains(trigger)) return
    const action = trigger.getAttribute('data-loc-action')
    if (action === 'show') {
      const row = trigger.closest('[data-loc-id]')
      if (row) showOnMap(row.getAttribute('data-loc-id'))
    } else if (action === 'show-nearest') {
      const id = trigger.getAttribute('data-loc-target')
      if (id) {
        const entry = state.entries.get(id)
        if (entry && entry.row.hidden) {
          state.query = ''
          ui.search.value = ''
          ui.searchClear.hidden = true
          state.filter = 'all'
          applyFilters({ fit: false })
        }
        showOnMap(id)
      }
    } else if (action === 'clear-search') {
      clearSearch()
    } else if (action === 'show-all') {
      state.filter = 'all'
      applyFilters({ fit: true })
      const chip = ui.chips.find((item) => item.dataset.locFilter === 'all')
      if (chip) chip.focus()
    } else if (action === 'retry-locate') {
      locate()
    }
  })
}

async function init() {
  let locations = []
  try {
    locations = cleanLocations(await getLocations())
  } catch {
    locations = []
  }
  if (locations.length === 0) locations = readFromDom(ui.list)
  if (locations.length === 0) {
    showMapFallback('Die Standorte laden gerade nicht. Versuch es gleich nochmal.')
    return
  }
  state.locations = locations
  renderRows()
  bindControls()
  ui.tools.hidden = false
  applyFilters({ fit: false })
  handleDeepLink()
  setupMap()
}

function boot() {
  const root = document.querySelector('[data-loc-page]')
  if (!root) return
  ui.root = root
  ui.tools = root.querySelector('[data-loc-tools]')
  ui.searchForm = root.querySelector('[data-loc-search-form]')
  ui.search = root.querySelector('[data-loc-search]')
  ui.searchClear = root.querySelector('[data-loc-search-clear]')
  ui.locate = root.querySelector('[data-loc-locate]')
  ui.chips = Array.from(root.querySelectorAll('[data-loc-filter]'))
  ui.hint = root.querySelector('[data-loc-hint]')
  ui.result = root.querySelector('[data-loc-result]')
  ui.resetSort = root.querySelector('[data-loc-reset-sort]')
  ui.list = root.querySelector('[data-loc-list]')
  ui.empty = root.querySelector('[data-loc-empty]')
  ui.mapFrame = root.querySelector('[data-loc-map-frame]')
  ui.mapCanvas = root.querySelector('[data-loc-map]')
  ui.mapNote = root.querySelector('[data-loc-map-note]')
  ui.announce = root.querySelector('[data-loc-announce]')
  const required = ['tools', 'searchForm', 'search', 'searchClear', 'locate', 'hint', 'result', 'resetSort', 'list', 'empty']
  if (required.some((key) => !ui[key])) return
  init().catch((err) => console.error('[keepsy/standorte] Seite konnte nicht starten.', err))
}

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true })
  } else {
    boot()
  }
}
